import React, { useCallback, useMemo, useState } from 'react'
import Text from 'core/elements/Text'
import Button from 'core/elements/button'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { deleteVolumeType, listAllTenantDefaultVolumeType } from './actions'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Modal from 'core/elements/modal'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { allDefaultVolumeTypeSelector } from './selectors'
import Progress from 'core/components/progress/Progress'
import Info from 'core/components/validatedForm/Info'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import ShowActionState from 'core/components/ShowActionState'

export default function DeleteVolumeTypeDialog({ rows: volumeTypes, onClose }) {
  const classes = useStyles()
  const [error, setError] = useState(null)
  const { update: deleteFn, updating: deletingVolumeItems } = useUpdateAction(deleteVolumeType)
  const { loading: loadingDefaultVolumeType } = useListAction(listAllTenantDefaultVolumeType)
  const allDefaultVolumeType = useSelector(allDefaultVolumeTypeSelector)

  // This contains the names of volume types that are used as default for some tenants
  const defaultVolumeTypes = useMemo(() => {
    if (volumeTypes?.length > 0 && allDefaultVolumeType?.length > 0) {
      return volumeTypes
        ?.filter((type) =>
          allDefaultVolumeType?.some((defaultType) => defaultType?.volume_type_id === type?.id),
        )
        ?.map((volumeType) => volumeType?.name || volumeType?.id)
        .join(', ')
    }
    return ''
  }, [volumeTypes, allDefaultVolumeType])

  const volumeTypeNames = volumeTypes?.map((volumeType) => ({
    name: volumeType?.name || volumeType?.id,
    status: '',
    actionName: '',
  }))

  const handleDelete = useCallback(async () => {
    setError(null)
    try {
      const results = await Promise.all(
        volumeTypes?.map(async (volumeType) => {
          try {
            const result = await deleteFn(volumeType)
            return { success: true, ...result }
          } catch (error) {
            return {
              success: false,
              error, // store the full error object
            }
          }
        }),
      )

      const allSuccess = results?.every((result) => result.success)

      if (allSuccess) {
        onClose(true)
      } else {
        setError({
          title: 'Error Deleting Volume Type',
          message: results
            ?.filter((r) => !r.success)
            ?.map(
              (r) =>
                r.error?.response?.data?.badRequest?.message ||
                r.error?.response?.data?.message ||
                r.error?.message ||
                'Unknown error',
            )
            ?.join(', '),
        })
      }
    } catch (err) {
      setError({
        title: 'Unexpected Error',
        message: err?.message || 'Something went wrong. Please try again.',
      })
    }
  }, [volumeTypes, deleteFn, onClose])

  return (
    <Modal
      panel="dialog"
      title="Delete Volume Type"
      onClose={onClose}
      open
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={deletingVolumeItems || loadingDefaultVolumeType}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} loading={deletingVolumeItems}>
            Confirm
          </Button>
        </>
      }
    >
      <>
        <Progress loading={loadingDefaultVolumeType} inline>
          {defaultVolumeTypes && (
            <Info warning className={classes.warningContainer}>
              <div className={classes.warning}>
                <Text variant="body2">
                  <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
                  <b>WARNING</b>
                </Text>
                <Text variant="body2">
                  {defaultVolumeTypes?.includes(',') ? (
                    <>
                      The volume types <strong>{defaultVolumeTypes}</strong> are used as default for
                      some tenants. Deleting them may affect their functionality.
                    </>
                  ) : (
                    <>
                      The volume type <strong>{defaultVolumeTypes}</strong> is used as default for
                      some tenants. Deleting it may affect their functionality.
                    </>
                  )}
                </Text>
              </div>
            </Info>
          )}
          <ShowActionState
            title="You are about to delete the following:"
            results={volumeTypeNames}
          />
          {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
        </Progress>
      </>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  warningContainer: { marginBottom: theme.spacing(2) },
  warning: { display: 'grid', gap: theme.spacing(1) },
  icon: { marginRight: theme.spacing(1) },
}))
